import { Controller } from "@hotwired/stimulus"

// Client-side search + display CAP for the "Browse all" section of the shared
// Common::BackgroundPickerComponent. The library is hundreds of backgrounds (and
// growing to 1000+), so we never show them all at once: at most `cap` matching
// cards are displayed; the rest stay hidden until the user refines the search.
//
// Two preview-loading modes:
//   * Deferred (data-src) — cards carry the URL but only fetch the image once
//     shown (legacy default; the URL is still rendered server-side).
//   * Lazy (urlValue set) — cards render image-LESS (data-bg-asset-id only); when
//     a card is shown we batch-resolve its preview URL from BackgroundPreviews
//     and set the <img> src. This lets the page skip eager-loading the whole
//     catalogue's ActiveStorage attachments entirely.
//
// Targets:
//   input         — the <input type="search">
//   card          — every <label class="bgpicker__card"> in the browse section
//   categoryGroup — the <div.bgpicker__category> wrapper (hidden when empty)
//   count         — a status line, e.g. "Showing 30 of 412 — refine to see more"
export default class extends Controller {
  static targets = ["input", "card", "categoryGroup", "count"]
  static values = { cap: { type: Number, default: 30 }, url: String }

  connect() {
    // id (string) -> resolved preview URL ("" = resolved-but-missing, so we don't refetch).
    this.urlCache = new Map()
    this.apply()
  }

  filter() {
    this.apply()
  }

  apply() {
    const term = (this.hasInputTarget ? this.inputTarget.value : "").trim().toLowerCase()
    const cap = this.capValue
    let matched = 0
    let shown = 0

    this.cardTargets.forEach((card) => {
      const haystack = [
        card.dataset.bgpickerName || "",
        card.dataset.bgpickerSlug || "",
        card.dataset.bgpickerCategory || "",
      ].join(" ")
      const isMatch = !term || haystack.includes(term)

      if (isMatch && shown < cap) {
        card.hidden = false
        shown += 1
        this.revealImage(card)
      } else {
        card.hidden = true
      }
      if (isMatch) matched += 1
    })

    // Hide category headings whose entire grid is now hidden.
    this.categoryGroupTargets.forEach((group) => {
      const anyVisible = Array.from(
        group.querySelectorAll('[data-bg-picker-search-target="card"]')
      ).some((c) => !c.hidden)
      group.hidden = !anyVisible
    })

    if (this.hasCountTarget) {
      if (matched === 0) {
        this.countTarget.textContent = ""
      } else if (matched > cap) {
        this.countTarget.textContent = `Showing ${cap} of ${matched} — refine your search to see more`
      } else {
        this.countTarget.textContent = `${matched} background${matched === 1 ? "" : "s"}`
      }
    }

    this.element.dataset.bgPickerSearchEmpty = matched === 0 ? "true" : "false"
    this.resolveLazyImages()
  }

  // Swap a deferred preview (data-src) to a real src once its card is shown.
  revealImage(card) {
    const img = card.querySelector("img[data-src]")
    if (img) {
      img.src = img.dataset.src
      img.removeAttribute("data-src")
    }
  }

  // Lazy mode: collect shown cards whose image isn't resolved yet, serve any
  // cached URLs immediately, and batch-fetch the rest from BackgroundPreviews.
  resolveLazyImages() {
    if (!this.hasUrlValue || !this.urlValue) return

    const pending = []
    this.cardTargets.forEach((card) => {
      if (card.hidden) return
      const img = card.querySelector("img[data-bg-lazy-img]")
      const id = card.dataset.bgAssetId
      if (!img || !id || img.src) return

      if (this.urlCache.has(id)) {
        this.applyLazyUrl(card, img, this.urlCache.get(id))
      } else {
        pending.push(id)
      }
    })

    if (pending.length) this.fetchPreviewUrls([...new Set(pending)].slice(0, 60))
  }

  async fetchPreviewUrls(ids) {
    try {
      // urlValue may already carry a query string (video_config_id for
      // client-workspace scoping) — append with the right separator.
      const sep = this.urlValue.includes("?") ? "&" : "?"
      const res = await fetch(`${this.urlValue}${sep}ids=${ids.join(",")}`, {
        headers: { Accept: "application/json" },
      })
      if (!res.ok) return
      const data = await res.json()
      const urls = (data && data.urls) || {}
      // Cache hits AND misses (so a missing preview isn't refetched every keystroke).
      ids.forEach((id) => this.urlCache.set(id, urls[id] || ""))
    } catch (_e) {
      return // network error — leave placeholders; a later keystroke retries.
    }
    // Apply to whatever is still shown now that the URLs are in.
    this.cardTargets.forEach((card) => {
      if (card.hidden) return
      const img = card.querySelector("img[data-bg-lazy-img]")
      const id = card.dataset.bgAssetId
      if (img && id && !img.src && this.urlCache.has(id)) {
        this.applyLazyUrl(card, img, this.urlCache.get(id))
      }
    })
  }

  applyLazyUrl(card, img, url) {
    if (!url) return
    img.src = url
    img.style.opacity = "1"
    // Populate the radio's preview URL so selecting the card drives the live preview.
    const radio = card.querySelector('input[type="radio"]')
    if (radio) radio.dataset.bgPreviewUrl = url
  }
}
